import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { CalendarMonth } from '@mui/icons-material';
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography
} from '@mui/material';
import { formatEndYearMonth } from '../utils/performanceMonth';

const PerformanceMonthPicker = ({ value, options, onChange, disabled }) => {
  const isDarkMode = document?.body?.classList.contains('dark');

  const formattedOptions = useMemo(
    () =>
      (options || []).map(option => ({
        value: option,
        label: formatEndYearMonth(option)
      })),
    [options]
  );

  return (
    <Stack
      direction="row"
      spacing={1.5}
      alignItems="center"
      sx={{ width: { xs: '100%', sm: 'auto' } }}
    >
      <CalendarMonth
        sx={{
          color: isDarkMode ? '#cbd5e1' : '#475569'
        }}
      />
      <FormControl
        size="small"
        disabled={disabled || !formattedOptions.length}
        sx={{
          minWidth: { xs: '100%', sm: 220 },
          '& .MuiInputLabel-root': {
            color: isDarkMode ? '#94a3b8' : '#64748b'
          },
          '& .MuiOutlinedInput-root': {
            backgroundColor: isDarkMode ? '#24303f' : '#ffffff',
            color: isDarkMode ? '#f8fafc' : '#0f172a',
            borderRadius: '12px',
            '& fieldset': {
              borderColor: isDarkMode ? '#2e3a47' : '#cbd5e1'
            },
            '&:hover fieldset': {
              borderColor: isDarkMode ? '#60a5fa' : '#3b82f6'
            },
            '&.Mui-focused fieldset': {
              borderColor: '#3b82f6'
            }
          },
          '& .MuiSvgIcon-root': {
            color: isDarkMode ? '#cbd5e1' : '#475569'
          }
        }}
      >
        <InputLabel id="performance-month-picker-label">Month</InputLabel>
        <Select
          labelId="performance-month-picker-label"
          value={value || ''}
          label="Month"
          onChange={event => onChange(event.target.value)}
          displayEmpty
          renderValue={selected => {
            if (!selected) {
              return (
                <Typography color="text.secondary">Select month</Typography>
              );
            }

            return formatEndYearMonth(selected);
          }}
          MenuProps={{
            PaperProps: {
              sx: {
                maxHeight: 320
              }
            }
          }}
        >
          {formattedOptions.map(option => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Stack>
  );
};

PerformanceMonthPicker.propTypes = {
  value: PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.string),
  onChange: PropTypes.func,
  disabled: PropTypes.bool
};

PerformanceMonthPicker.defaultProps = {
  value: '',
  options: [],
  onChange: () => {},
  disabled: false
};

export default PerformanceMonthPicker;
